/**
 * Feature flag utility for Hanko Menu.
 *
 * Fetches boolean flags from the myvaisala-fe namespace in Flipt
 * via the BFF proxy. Flags are fetched once and cached in memory.
 *
 * Usage:
 *   await initFeatureFlags();
 *   const isEnabled = getFlag('enableSingleLogout');
 */

import { resolveEnv } from '@/config/environments';
import { credentialedFetch } from '@/utils/fetch';

const { bffApiUrl } = resolveEnv();

const NAMESPACE = 'myvaisala-fe';
const FETCH_TIMEOUT_MS = 5_000;

interface FliptFlag {
  key: string;
  enabled: boolean;
  type: string;
}

interface FliptFlagsResponse {
  flags: FliptFlag[];
}

const flagCache = new Map<string, boolean>();
// Shared in-flight promise so concurrent callers reuse one request.
let initPromise: Promise<void> | null = null;

async function fetchFlags(): Promise<void> {
  try {
    const url = import.meta.env.DEV
      ? `/flipt/api/v1/namespaces/${NAMESPACE}/flags`
      : `${bffApiUrl}/api/v1/feature-flags?namespace=${NAMESPACE}`;

    const response = await credentialedFetch(url, { timeoutMs: FETCH_TIMEOUT_MS });

    if (!response.ok) {
      console.warn('[FeatureFlags] Fetch failed:', response.status);
      return;
    }

    const data: FliptFlagsResponse = await response.json();
    for (const flag of data.flags) {
      if (flag.type === 'BOOLEAN_FLAG_TYPE') {
        flagCache.set(flag.key, flag.enabled);
      }
    }
  } catch {
    console.warn('[FeatureFlags] Fetch error, all flags will use defaults');
  }
}

/**
 * Fetches all boolean flags from the myvaisala-fe namespace.
 * Call once at startup. Safe to call multiple times and concurrently —
 * only one request is ever made.
 */
export function initFeatureFlags(): Promise<void> {
  initPromise ??= fetchFlags();
  return initPromise;
}

/**
 * Returns the cached value of a boolean flag.
 * Returns defaultValue if the flag was not found or flags haven't been fetched.
 */
export function getFlag(key: string, defaultValue = false): boolean {
  return flagCache.get(key) ?? defaultValue;
}
